import virtualPosService from './virtualPosService.js';

/**
 * Listado de suscripciones VirtualPOS: una página a la vez.
 *
 * Nada que crezca con el total sale de aquí. Con 500 o con 15.000 suscripciones
 * se piden siempre `limite` filas, porque el costo es por página y no por total.
 *
 * Dos fuentes, según lo que se esté haciendo:
 *
 * - **Navegando** (sin texto de búsqueda): manda VirtualPOS, que pagina del lado
 *   del servidor (`page`/`limit`/`status`) y es la verdad sobre el estado. Una
 *   página de 20 tarda ~2 s; pedir las 507 de una tardaba ~60 s. La base solo
 *   completa código, RUT y certificados/contrataciones de esas filas.
 *
 * - **Buscando**: manda la base, vía `buscar_suscripciones_virtualpos`.
 *   VirtualPOS no sabe buscar por código de certificado, de contratación ni por
 *   RUT —esos datos viven en divina—, así que la búsqueda se resuelve en
 *   Postgres y VirtualPOS solo aporta el estado de las filas de la página.
 *
 * Consecuencia a tener presente: una suscripción que exista en VirtualPOS pero
 * no en la base no aparece al buscar (sí al navegar, con "BD: Sin registro").
 */

export const FILAS_POR_PAGINA = 20;

export const ESTADOS_VIRTUALPOS = [
  'ACTIVA',
  'CANCELADA',
  'SUSCRIBIENDO',
  'SUSCRIPCION_FALLIDA',
  'FINALIZADA',
  'EXPIRADA',
];

// VirtualPOS encola las peticiones: con demasiadas a la vez todas se pasan del
// timeout. De a 5 se gana bastante sobre ir en cadena sin ahogarlo.
const CONCURRENCIA = 5;

/*
 * Caché de páginas ya pedidas. VirtualPOS tarda ~2 s por página y el listado no
 * cambia de un segundo a otro: volver atrás, repetir una búsqueda o remontar el
 * componente no justifica pagar esos 2 s otra vez. El botón "Actualizar" la
 * saltea (`sinCache`) y a los 60 s se considera vieja.
 */
const TTL_CACHE_MS = 60_000;
const cachePaginas = new Map();

const claveCache = ({ pagina, texto, estado, limite }) =>
  `${pagina}|${String(texto || '').trim()}|${estado || ''}|${limite}`;

const leerCache = (k) => {
  const hit = cachePaginas.get(k);
  if (!hit) return null;
  if (Date.now() - hit.guardadoEn > TTL_CACHE_MS) {
    cachePaginas.delete(k);
    return null;
  }
  return hit.valor;
};

/*
 * Peticiones en vuelo. Si se pide dos veces la misma página antes de que llegue
 * la primera, VirtualPOS encola la segunda detrás de la primera y la espera se
 * duplica (medido: 1,9 s la primera, 3,9 s la segunda). Compartir la promesa
 * evita el trabajo repetido — lo provoca el remontaje del componente y, en
 * desarrollo, el doble efecto de StrictMode.
 */
const enVuelo = new Map();

/** Invalida lo cacheado. Usarlo después de cancelar o de cualquier escritura. */
export const invalidarCache = () => {
  cachePaginas.clear();
  enVuelo.clear();
};

/**
 * Lo cacheado para esa página, o null. Es síncrono a propósito: deja pintar la
 * tabla en el primer render, sin esperar a que Clerk resuelva la sesión — que es
 * lo que hacía que volver a la pantalla tardara ~2,5 s aun sin pedir nada.
 */
export const leerPaginaCacheada = ({
  pagina = 1,
  texto = '',
  estado = '',
  limite = FILAS_POR_PAGINA,
} = {}) => leerCache(claveCache({ pagina, texto, estado, limite }));

const extraerFilas = (respuesta) => (
  Array.isArray(respuesta)
    ? respuesta
    : respuesta?.suscriptions
      || respuesta?.subscriptions
      || respuesta?.data?.suscriptions
      || respuesta?.data?.subscriptions
      || respuesta?.data
      || []
);

const extraerTotal = (respuesta) => {
  const total = Number(respuesta?.pagination?.total ?? respuesta?.data?.pagination?.total);
  return Number.isFinite(total) ? total : null;
};

/** Normaliza una suscripción tal como la devuelve VirtualPOS. */
export const adaptarSuscripcion = (sub) => ({
  id: sub?.id,
  status: sub?.status,
  planName: sub?.plan_name,
  subscriptionDate: sub?.suscription_date,
  currency: sub?.currency,
  amount: sub?.amount,
  renewal: sub?.renewal,
  client: {
    email: sub?.client?.email,
    firstName: sub?.client?.first_name,
    lastName: sub?.client?.last_name,
    socialId: sub?.client?.social_id,
    phone: sub?.client?.phone,
  },
  paymentMethod: sub?.payment_method,
  last4CardDigit: sub?.payment_method?.last4CardDigit,
  origin: sub?.origin,
});

/** Normaliza una fila de la base (RPC) a lo que consume la tabla. */
const adaptarFilaBd = (bd) => ({
  id: bd?.id || null,
  codigo: bd?.codigo || null,
  status: bd?.status || null,
  companiaNombre: bd?.compania_nombre || null,
  companiaRut: bd?.compania_rut || null,
  planName: bd?.plan_name || null,
  certificadoCodigos: bd?.certificado_codigos || [],
  contratacionCodigos: bd?.contratacion_codigos || [],
  contratacionNombres: bd?.contratacion_nombres || [],
  contratacionRuts: bd?.contratacion_ruts || [],
});

/** Corre `fn` sobre `items` de a `limite` en paralelo. */
const mapConLimite = async (items, limite, fn) => {
  const salida = [];
  for (let i = 0; i < items.length; i += limite) {
    const lote = await Promise.all(items.slice(i, i + limite).map(fn));
    salida.push(...lote);
  }
  return salida;
};

/**
 * Datos de la base para un conjunto de ids de VirtualPOS.
 * @returns {Promise<Object>} mapa external_subscription_id → fila normalizada
 */
export const enriquecerDesdeBd = async (supabase, externalIds) => {
  if (!Array.isArray(externalIds) || externalIds.length === 0) return {};
  const { data, error } = await supabase.rpc('buscar_suscripciones_virtualpos', {
    p_busqueda: null,
    p_estado: null,
    p_limite: externalIds.length,
    p_offset: 0,
    p_external_ids: externalIds,
  });
  if (error) throw error;

  const porExternalId = {};
  (data || []).forEach((fila) => {
    if (fila?.external_subscription_id) {
      porExternalId[String(fila.external_subscription_id)] = adaptarFilaBd(fila);
    }
  });
  return porExternalId;
};

async function listarNavegando({ supabase, pagina, estado, limite }) {
  const respuesta = await virtualPosService.getSubscriptions({
    page: pagina,
    limit: limite,
    ...(estado ? { status: estado } : {}),
  });

  const filas = extraerFilas(respuesta).map(adaptarSuscripcion);
  const externalIds = filas.map((f) => String(f.id)).filter(Boolean);

  // Si falla la base devolvemos igual el listado y avisamos: es peor una tabla
  // vacía que una sin los códigos.
  let porExternalId = {};
  let errorBd = null;
  try {
    porExternalId = await enriquecerDesdeBd(supabase, externalIds);
  } catch (error) {
    errorBd = error;
  }

  return {
    filas: filas.map((f) => ({ ...f, _bd: porExternalId[String(f.id)] || null })),
    total: extraerTotal(respuesta) ?? filas.length,
    errorBd,
  };
}

async function listarBuscando({ supabase, pagina, busqueda, limite }) {
  const { data, error } = await supabase.rpc('buscar_suscripciones_virtualpos', {
    p_busqueda: busqueda,
    p_estado: null,
    p_limite: limite,
    p_offset: (pagina - 1) * limite,
    p_external_ids: null,
  });
  if (error) throw error;

  const encontradas = data || [];
  const total = encontradas.length > 0 ? Number(encontradas[0].total_filas) || 0 : 0;

  // El estado real lo tiene VirtualPOS: son ≤`limite` consultas por id.
  const filas = await mapConLimite(encontradas, CONCURRENCIA, async (bd) => {
    const enBd = adaptarFilaBd(bd);
    const externalId = bd?.external_subscription_id ? String(bd.external_subscription_id) : null;

    // Lo que se puede mostrar solo con la base, por si VirtualPOS no responde.
    const base = {
      id: externalId || String(bd?.id || ''),
      status: null,
      planName: enBd.planName,
      subscriptionDate: bd?.created_at || null,
      amount: bd?.amount ?? null,
      currency: bd?.currency || null,
      client: {
        firstName: enBd.contratacionNombres[0] || null,
        lastName: null,
        socialId: enBd.contratacionRuts[0] || null,
        email: null,
      },
      _bd: enBd,
    };

    if (!externalId) return base;
    try {
      const detalle = await virtualPosService.getSubscriptionById(externalId);
      const vp = detalle?.suscription || detalle?.data?.suscription;
      return vp ? { ...adaptarSuscripcion(vp), _bd: enBd } : base;
    } catch (vpError) {
      // Está en la base pero VirtualPOS no la devuelve: mostramos lo que hay.
      console.warn('No se pudo leer el estado en VirtualPOS de', externalId, vpError?.message);
      return base;
    }
  });

  return { filas, total, errorBd: null };
}

/**
 * Una página del listado.
 *
 * @param {object} opciones
 * @param {object} opciones.supabase  cliente autenticado (Clerk): `certificados` tiene RLS
 * @param {number} opciones.pagina    1-indexada
 * @param {string} opciones.texto     búsqueda libre; vacío ⇒ modo navegación
 * @param {string} opciones.estado    estado VirtualPOS; solo aplica navegando
 * @param {number} opciones.limite
 * @returns {Promise<{filas: Array, total: number, errorBd: Error|null}>}
 */
export const listarSuscripciones = async ({
  supabase,
  pagina = 1,
  texto = '',
  estado = '',
  limite = FILAS_POR_PAGINA,
  sinCache = false,
}) => {
  const k = claveCache({ pagina, texto, estado, limite });
  if (!sinCache) {
    const cacheado = leerCache(k);
    if (cacheado) return cacheado;
    const yaPedida = enVuelo.get(k);
    if (yaPedida) return yaPedida;
  }

  const busqueda = String(texto || '').trim();
  const promesa = (busqueda
    ? listarBuscando({ supabase, pagina, busqueda, limite })
    : listarNavegando({ supabase, pagina, estado, limite })
  ).then((resultado) => {
    // Si la base falló, la fila queda sin código ni certificados: no cachear
    // algo incompleto, o el error se queda pegado un minuto.
    if (!resultado.errorBd) cachePaginas.set(k, { valor: resultado, guardadoEn: Date.now() });
    return resultado;
  }).finally(() => {
    enVuelo.delete(k);
  });

  enVuelo.set(k, promesa);
  return promesa;
};

/**
 * Totales para los KPI. Salen del `total` que ya informa la paginación de
 * VirtualPOS, así que son 3 consultas de una fila y no dependen del volumen.
 */
export const contarPorEstado = async () => {
  const [todas, activas, canceladas] = await Promise.all([
    virtualPosService.getSubscriptions({ page: 1, limit: 1 }),
    virtualPosService.getSubscriptions({ page: 1, limit: 1, status: 'ACTIVA' }),
    virtualPosService.getSubscriptions({ page: 1, limit: 1, status: 'CANCELADA' }),
  ]);
  return {
    total: extraerTotal(todas),
    activas: extraerTotal(activas),
    canceladas: extraerTotal(canceladas),
  };
};

export default {
  FILAS_POR_PAGINA,
  ESTADOS_VIRTUALPOS,
  listarSuscripciones,
  enriquecerDesdeBd,
  contarPorEstado,
  adaptarSuscripcion,
  invalidarCache,
  leerPaginaCacheada,
};
